import { useState, type FormEvent } from 'react';
import { entryInput, type EntryUpdateInput } from '@redirector/shared';
import { Checkbox } from '../components/ui/checkbox.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { Field } from '../ui/Field.js';
import { issuesToFieldErrors, type FieldErrors } from '../ui/formErrors.js';
import { useGroups } from '../groups/queries.js';
import { parseFullUrl, sameHost } from './parseUrl.js';
import { useDomainsAcrossGroups } from './queries.js';

export type UrlIdentityFormProps = {
  /** The hostname the entry is on now, so a hostname EDIT can be recognised. */
  currentHostname: string;
  initial: { url: string; label: string; enabled: boolean };
  submitLabel: string;
  /**
   * Rendered as a dialog body or as a band across the top of the URL page.
   *
   * Only the chrome differs — the same field validation, the same submit — so
   * the two places a URL is edited cannot drift apart in what they accept.
   */
  layout?: 'dialog' | 'inline';
  onSubmit: (input: EntryUpdateInput) => Promise<void>;
  onCancel: () => void;
};

const MONO = 'font-mono text-[length:var(--text-small)]';

/**
 * Editing what a URL IS: its hostname, its path, its label, its enabled flag.
 *
 * The hostname used to be immovable — the form showed a path box under a fixed
 * hostname — which made the commonest correction in this console impossible to
 * do without destroying something. A URL typed against the wrong client, or a
 * hostname that changed, could only be fixed by deleting the entry and adding
 * it again, and deleting it soft-deletes the row every recorded redirect points
 * at. The operator lost the traffic history to fix a typo.
 *
 * So the field here is the whole URL, exactly as it is displayed and exactly as
 * `AddUrlDialog` accepts it, and a changed hostname MOVES the entry: the server
 * re-points it at the domain for that hostname, creating one if nobody has
 * configured it yet. The entry keeps its id, so it keeps its history.
 *
 * A hostname nobody has configured yet needs a group, and that question is
 * asked here rather than assumed, because the wrong answer files a client's URL
 * under another client and nothing about the screen would look wrong.
 */
export function UrlIdentityForm({
  currentHostname,
  initial,
  submitLabel,
  layout = 'dialog',
  onSubmit,
  onCancel,
}: UrlIdentityFormProps) {
  const groups = useGroups();
  const domains = useDomainsAcrossGroups();

  const [fields, setFields] = useState(initial);
  const [groupId, setGroupId] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);

  const parsed = fields.url.trim() === '' ? null : parseFullUrl(fields.url);
  const parts = parsed?.ok === true ? parsed.parts : null;

  const movingHost = parts !== null && !sameHost(parts.hostname, currentHostname);
  const target =
    parts === null
      ? undefined
      : domains.data?.domains.find((domain) => sameHost(domain.hostname, parts.hostname));
  /* A hostname nobody has configured is a new domain, and a new domain has to
     be filed under a group. Only asked when the hostname is actually changing:
     staying put needs no answer. */
  const needsGroup = movingHost && target === undefined;

  const dirty =
    fields.url.trim() !== initial.url.trim() ||
    fields.label !== initial.label ||
    fields.enabled !== initial.enabled;

  async function submit(event: FormEvent) {
    event.preventDefault();

    if (parsed === null || !parsed.ok) {
      setErrors({ url: parsed === null ? 'A URL is required' : parsed.message });
      return;
    }

    if (needsGroup && groupId === '') {
      setErrors({ group: 'Choose the group this new hostname belongs to' });
      return;
    }

    const entry = entryInput.safeParse({
      label: fields.label,
      path: parsed.parts.path,
      enabled: fields.enabled,
    });
    if (!entry.success) {
      const issues = issuesToFieldErrors(entry.error.issues);
      setErrors(issues.path === undefined ? issues : { ...issues, url: issues.path });
      return;
    }

    setErrors({});
    setBusy(true);
    try {
      await onSubmit({
        ...entry.data,
        hostname: parsed.parts.hostname,
        ...(needsGroup ? { groupId: Number(groupId) } : {}),
      });
    } catch (error) {
      /* The server names a path collision on `path` and a hostname problem on
         `hostname`, but this form has one box covering both, so both land on
         it — complaining about a field the operator cannot see is worse than
         no message at all. */
      const fromServer =
        error !== null && typeof error === 'object' && 'fields' in error
          ? (error as { fields: FieldErrors }).fields
          : {};
      const onUrl = fromServer.path ?? fromServer.hostname;
      if (onUrl !== undefined) setErrors({ url: onUrl });
      else if (Object.keys(fromServer).length > 0) setErrors(fromServer);
      else setErrors({ root: error instanceof Error ? error.message : 'Could not save the URL' });
    } finally {
      setBusy(false);
    }
  }

  const inline = layout === 'inline';

  return (
    <form onSubmit={submit} className="flex flex-col gap-(--space-4)" noValidate>
      {errors.root !== undefined && <Callout tone="danger">{errors.root}</Callout>}

      <Field
        label="Full URL"
        error={errors.url}
        hint="Hostname and path together, for example timpromos.com.br/BTW/summer. Changing the hostname moves this URL to that domain and keeps its traffic history. The scheme is ignored; no query string."
      >
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            className={
              inline
                ? 'h-auto py-(--space-2) font-mono text-[length:var(--text-lead)] font-bold'
                : MONO
            }
            spellCheck={false}
            autoCapitalize="none"
            inputMode="url"
            aria-describedby={describedBy}
            aria-invalid={invalid}
            value={fields.url}
            onChange={(event) => setFields((prev) => ({ ...prev, url: event.target.value }))}
          />
        )}
      </Field>

      {/* Said out loud, because a hostname edit is not a rename — it re-files
          the URL under a different domain, and possibly a different client. */}
      {movingHost && target !== undefined && (
        <p className="text-[length:var(--text-small)] text-ink-muted">
          Saving moves this URL to <span className="font-mono">{target.hostname}</span>, in{' '}
          {target.groupName}. Its traffic history comes with it.
        </p>
      )}

      <Field label="Label" error={errors.label} hint="Optional. What this campaign is, in human terms.">
        {({ id, describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            value={fields.label}
            onChange={(event) => setFields((prev) => ({ ...prev, label: event.target.value }))}
          />
        )}
      </Field>

      {needsGroup && (
        <Field
          label="Group"
          error={errors.group}
          hint="This hostname is not configured yet, so it will be created under the group you choose."
        >
          {({ id, describedBy, invalid }) => (
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                className="w-full"
                disabled={groups.data === undefined}
              >
                <SelectValue placeholder="Choose a group" />
              </SelectTrigger>
              <SelectContent>
                {(groups.data?.groups ?? []).map((group) => (
                  <SelectItem key={group.id} value={String(group.id)}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      )}

      <Label htmlFor="entry-enabled" className="font-normal">
        <Checkbox
          id="entry-enabled"
          checked={fields.enabled}
          onCheckedChange={(next) => setFields((prev) => ({ ...prev, enabled: next === true }))}
        />
        Enabled — when off, this path falls through to the fallback
      </Label>

      <div className="flex justify-end gap-(--space-2)">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          {inline ? 'Revert' : 'Cancel'}
        </Button>
        {/* Inline, an unchanged form has nothing to save, and a live Save
            button on a page the operator is only reading invites a pointless
            write. In a dialog the operator opened it on purpose. */}
        <Button type="submit" variant="primary" disabled={busy || (inline && !dirty)}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}
